/**
 * Proxy Contract — Mastery → Unlock Projection (FG2: UI-Grind vs ELO)
 *
 * ARCHITECTURAL_PEER_REVIEW_PLAN_2026 §F0.A + §FG2 (Phases 1 + 2).
 *
 * THE PROBLEM
 * ===========
 * Two systems tell a different truth about the same learner:
 *   - `familyMasteryEngine` — holistic, forgiving ELO score (skill).
 *   - `tierProgress`        — per-question attempt counter (completeness).
 * A fast learner reaches high Family-ELO but the UI bar stays low because
 * every easy variant must still be individually "tapped". The competent
 * learner feels the strictest path — cognitive dissonance as a feature.
 *
 * THE CONTRACT
 * ============
 * This is a PURE, dependency-inverted projection: it accepts already-computed
 * values (counts, ELO, breadth, spread) as inputs and returns an unlock
 * decision. It imports NEITHER the mastery engine NOR tierProgress — the
 * caller injects the facts (DDD boundary stays intact, FG2 §design-principe 1).
 *
 * Unlock is OR-logic with TWO paths (never a replacement):
 *   countPath  — existing grind path (slow learner).
 *   eloPath    — new fast path (fast learner), guarded against gaming by
 *                BREADTH (unique-fraction seen) + SPREAD (touched hardest band).
 *
 * PHASE 2 — Equivalence-class counting (FG2 §fase 2):
 *   For long flat-distribution families (e.g. REK-TAFELS-BASIS with 100+
 *   variants of essentially the same skill), the contract optionally
 *   accepts equivalence-class counts. When `totalEquivalenceClasses` and
 *   `uniqueEquivalenceClassesSeen` are both supplied AND
 *   `totalEquivalenceClasses > 0`, BREADTH is computed against the class
 *   denominator instead of the raw question count — collapsing the
 *   perceived path without lowering the actual mastery bar. When omitted,
 *   behaviour is identical to Phase 1 (per-question coverage). The
 *   returned `breadthBasis` exposes which mode applied so the UI and
 *   telemetry can communicate it honestly.
 */

export interface UnlockInputs {
  /** Existing grind metric: how many variants are practiced-or-mastered. */
  practicedOrMasteredCount: number;
  /** Total questions available in this family+tier. */
  totalQuestions: number;
  /** Ratio (0..1) of attempts required for the count path. (V11 LEVEL_UNLOCK_THRESHOLD) */
  levelUnlockThreshold: number;

  /** Family ELO / mastery score (0..100). Injected — NOT imported. */
  familyMasteryScore: number;
  /** Fast-path mastery threshold (e.g. 75). */
  masteryFastPath: number;
  /** Distinct questions the learner has actually seen in this family+tier. */
  uniqueQuestionsSeen: number;
  /** Minimum unique-fraction (0..1) required for the fast path (breadth guard, A3). */
  breadthGuardFraction: number;
  /** True when the learner has touched ≥1 question from the hardest difficulty band (spread guard, A2). */
  touchedHighestDifficultyBand: boolean;

  /**
   * FG2 Phase 2 — Total number of distinct `equivalenceClass` values among
   * the questions in scope (questions without an explicit class form a
   * singleton class keyed by their id). When supplied together with
   * `uniqueEquivalenceClassesSeen` AND > 0, coverage is computed against
   * this denominator instead of `totalQuestions`. Omit for Phase-1
   * behaviour (per-question coverage).
   */
  totalEquivalenceClasses?: number;
  /**
   * FG2 Phase 2 — Number of distinct equivalence classes for which the
   * learner has answered at least one representative. Required together
   * with `totalEquivalenceClasses` to activate class-based breadth.
   */
  uniqueEquivalenceClassesSeen?: number;
}
export interface UnlockState {
  unlocked: boolean;
  /** Which path granted the unlock (for telemetry + UI messaging). */
  via: 'count' | 'mastery' | 'none';
  /** Count-path progress ratio (0..1) for the UI "breadth" layer. */
  countProgress: number;
  /** Mastery-path readiness (0..1) for the UI "depth"/glow layer. */
  masteryProgress: number;
  /** Unique-fraction of variants seen (0..1) — drives "X varianten nog niet gezien". */
  coverage: number;
  /**
   * True when the learner has high mastery but coverage is still low — used
   * to surface a SUBTLE "X varianten nog niet gezien" hint, NOT a blocker.
   */
  coverageGapWithHighMastery: boolean;
  /**
   * FG2 Phase 2 — Which denominator drove `coverage`. `'equivalence-classes'`
   * when the caller supplied valid class counts; `'questions'` otherwise
   * (Phase-1 / backwards-compatible default). Telemetry and UI can use
   * this to honestly communicate "X classes mastered" vs "X variants seen".
   */
  breadthBasis: 'questions' | 'equivalence-classes';
}
function ratio(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.min(1, Math.max(0, part / whole));
}

/**
 * Project the unlock state from injected facts. Pure — same inputs always
 * yield the same output. No side effects, no imports of engine/tierProgress.
 */
export function projectMasteryToUnlock(input: UnlockInputs): UnlockState {
  const required = Math.ceil(input.totalQuestions * input.levelUnlockThreshold);
  const countPath = input.practicedOrMasteredCount >= required;

  // ── FG2 Phase 2: decide breadth denominator (classes if supplied, else
  // questions). Activation requires BOTH class fields AND a positive total.
  const useClassBasis = typeof input.totalEquivalenceClasses === 'number' && typeof input.uniqueEquivalenceClassesSeen === 'number' && input.totalEquivalenceClasses > 0;
  const coverage = useClassBasis ? ratio(input.uniqueEquivalenceClassesSeen as number, input.totalEquivalenceClasses as number) : ratio(input.uniqueQuestionsSeen, input.totalQuestions);
  const breadthBasis: UnlockState['breadthBasis'] = useClassBasis ? 'equivalence-classes' : 'questions';
  const eloPath = input.familyMasteryScore >= input.masteryFastPath && coverage >= input.breadthGuardFraction && input.touchedHighestDifficultyBand;
  const countProgress = ratio(input.practicedOrMasteredCount, required);
  const masteryProgress = ratio(input.familyMasteryScore, input.masteryFastPath);
  const unlocked = countPath || eloPath;
  const via: UnlockState['via'] = countPath ? 'count' : eloPath ? 'mastery' : 'none';
  const coverageGapWithHighMastery = !unlocked && input.familyMasteryScore >= input.masteryFastPath && coverage < input.breadthGuardFraction;
  return {
    unlocked,
    via,
    countProgress,
    masteryProgress,
    coverage,
    coverageGapWithHighMastery,
    breadthBasis
  };
}